import { useApi } from "@/hooks";
import { Actions, AppContext } from "@Context/common/general/context";
import { useDialogManager } from "@Context/common/general/dialog-manager-context";
import { useTrainingPlansProgressContext } from "@Context/game/training-plans/training-plans-progress-context";
import CrudActions from "@Enums/CrudActions";
import Flags from "@Enums/Flags";
import PlanTypes from "@Enums/PlanTypes";
import { TRAINING_PLANS_ONBOARDING_DIALOG, TrainingPlansOnboardingStep } from "@hooks/game/training-plans/training-plans-shared";
import { Api } from "@Shared/schemas";
import { PlanRecord } from "@Shared/schemas/training-plans/trainingPlanSchemas";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Auto-onboarding gatekeeper for Training Plans.
 *
 * Responsibility:
 * - Evaluates whether onboarding should be auto-opened for the current user.
 * - Runs eligibility + availability checks (auth/account/subscription + available plans + active enrollment state).
 * - Persists the onboarding-read flag when the user is eligible.
 */
export const useTrainingPlansOnboardingGate = () => {
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const { openDialog } = useDialogManager();
  const { canUseTrainingPlans, isProgressResolved, progressData, progressError } = useTrainingPlansProgressContext();
  const autoOpenedOnboardingRef = useRef(false);
  const [hasUnreadOnboardingFlag, setHasUnreadOnboardingFlag] = useState<boolean | null>(null);
  const hasCompletedBootcamp = !state.user.inBootcamp && location.pathname === "/home";

  const { call: getAvailableTrainingPlans } = useApi<typeof Api.TrainingPlans.GetPlans>({
    api: Api.TrainingPlans.GetPlans,
    request: {
      plan_type: PlanTypes.TRAINING,
    },
    disableLoader: true,
  });

  const checkGate = useCallback(async () => {
    if (autoOpenedOnboardingRef.current || !hasCompletedBootcamp || !isProgressResolved || progressError) {
      return;
    }

    try {
      const availablePlans = ((await getAvailableTrainingPlans()) ?? []) as PlanRecord[];

      if (progressData?.enrollment?.is_active || availablePlans.length === 0) {
        return;
      }

      autoOpenedOnboardingRef.current = true;
      await openDialog(TRAINING_PLANS_ONBOARDING_DIALOG, {
        isEditMode: false,
        step: TrainingPlansOnboardingStep.Intro,
        availablePlans,
        progressData,
      });
    } catch (error) {
      console.error("Could not auto-open training plans onboarding", error);
    }
  }, [getAvailableTrainingPlans, hasCompletedBootcamp, isProgressResolved, openDialog, progressData, progressError]);

  useEffect(() => {
    autoOpenedOnboardingRef.current = false;
    setHasUnreadOnboardingFlag(null);
  }, [state.account.id, state.user.id]);

  useEffect(() => {
    if (!canUseTrainingPlans || !hasCompletedBootcamp) {
      autoOpenedOnboardingRef.current = false;
      setHasUnreadOnboardingFlag(null);
      return;
    }

    if (!state.user.id) {
      return;
    }

    dispatch({
      type: Actions.UsersActions.CheckFlags,
      payload: {
        user_id: state.user.id,
        flag_id: Flags.TRAINING_PLANS_ONBOARDING_READ,
        action: CrudActions.REQUEST,
      },
      onSuccess: data => {
        setHasUnreadOnboardingFlag(data.code === "not-found");
      },
      onError: error => {
        console.error("Could not persist training plans onboarding flag", error);
      },
    });
  }, [canUseTrainingPlans, hasCompletedBootcamp, state.user.id]);

  useEffect(() => {
    if (!canUseTrainingPlans || !hasCompletedBootcamp || hasUnreadOnboardingFlag !== true || !isProgressResolved || progressError) {
      return;
    }

    void checkGate();
  }, [canUseTrainingPlans, checkGate, hasCompletedBootcamp, hasUnreadOnboardingFlag, isProgressResolved, progressError]);
};
